import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Ticket, Settings, Puzzle } from 'lucide-react';
import type { SchoolOptions } from '@/types/school';
import type { V2TenantGroup } from '@/types/v2-deployment';

export interface V2SubscriptionFormData {
  tenantGroupId?: string;
  offerId: number | null;
  optionIds: number[];
}

interface V2SubscriptionFormProps {
  options: SchoolOptions | null;
  tenantGroups?: V2TenantGroup[];
  defaultTenantGroupId?: string;
  value: V2SubscriptionFormData;
  onChange: (data: V2SubscriptionFormData) => void;
  showTenantGroup?: boolean;
  compact?: boolean;
}

export function V2SubscriptionForm({
  options,
  tenantGroups,
  defaultTenantGroupId,
  value,
  onChange,
  showTenantGroup = true,
  compact = false,
}: V2SubscriptionFormProps) {
  // Get selected offer
  const selectedOffer = useMemo(() => {
    return options?.offers.find((o) => o.id === value.offerId);
  }, [options?.offers, value.offerId]);

  const selectedProductCode = selectedOffer?.productCode || null;

  // Get options for selected product
  const optionsForProduct = useMemo(() => {
    if (!selectedProductCode || !options?.productOptions) return [];
    return options.productOptions.filter((o) => o.productCode === selectedProductCode);
  }, [options?.productOptions, selectedProductCode]);

  const handleOptionToggle = (optionId: number) => {
    const currentOptions = value.optionIds || [];
    const newOptions = currentOptions.includes(optionId)
      ? currentOptions.filter((id) => id !== optionId)
      : [...currentOptions, optionId];
    onChange({ ...value, optionIds: newOptions });
  };

  const selectedGroup = tenantGroups?.find((g) => g.id === value.tenantGroupId);
  const isDefaultGroup = value.tenantGroupId === defaultTenantGroupId;

  return (
    <div className={`space-y-4 ${compact ? '' : ''}`}>
      {/* Tenant Group Selection */}
      {showTenantGroup && tenantGroups && tenantGroups.length > 0 && (
        <div className="space-y-2">
          <Label>Reseau V2 (Tenant Group)</Label>
          <Select
            value={value.tenantGroupId || defaultTenantGroupId || ''}
            onValueChange={(val) => onChange({ ...value, tenantGroupId: val })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selectionner un reseau" />
            </SelectTrigger>
            <SelectContent>
              {tenantGroups.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name}
                  {group.id === defaultTenantGroupId ? ' (Par defaut)' : ''}
                  {group.tenantCount > 0 ? ` - ${group.tenantCount} écoles` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedGroup && (
            <p className="text-xs text-muted-foreground">
              Domaine: {selectedGroup.host || 'Non defini'}
            </p>
          )}
          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              {isDefaultGroup || !selectedGroup ? (
                <>
                  L'école sera créée dans le réseau <strong>École Futée</strong> par défaut.
                </>
              ) : (
                <>
                  L'école sera créée dans le réseau <strong>{selectedGroup.name}</strong>.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Offer Selection */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Ticket className="h-4 w-4" />
          Offre
        </Label>
        <Select
          value={value.offerId?.toString() || 'none'}
          onValueChange={(val) => {
            const offerId = val === 'none' ? null : parseInt(val);
            onChange({
              ...value,
              offerId,
              optionIds: [], // Reset options when offer changes
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selectionner une offre" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Selectionner une offre</SelectItem>
            {options?.offers.map((offer) => (
              <SelectItem key={offer.id} value={offer.id.toString()}>
                {offer.productName} - {offer.name} ({offer.moduleCount} modules)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Options - only show if offer is selected */}
      {selectedOffer && optionsForProduct.length > 0 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Options
          </Label>
          <div className="grid grid-cols-1 gap-2 border rounded-md p-2">
            {optionsForProduct.map((option) => (
              <div key={option.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`v2-option-${option.id}`}
                  checked={value.optionIds.includes(option.id)}
                  onCheckedChange={() => handleOptionToggle(option.id)}
                />
                <Label
                  htmlFor={`v2-option-${option.id}`}
                  className="text-sm font-normal cursor-pointer flex items-center gap-2"
                >
                  {option.name}
                  <Badge variant="outline" className="text-xs">
                    {option.type === 'recurring' ? 'Recurrent' : 'One-shot'}
                  </Badge>
                </Label>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modules - read-only display based on selected offer */}
      {selectedOffer && selectedOffer.modules.length > 0 && (
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Puzzle className="h-4 w-4" />
            Modules inclus ({selectedOffer.modules.length})
          </Label>
          <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/20">
            {selectedOffer.modules.map((module) => (
              <Badge key={module.id} variant="secondary">
                {module.name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
